<template>
  <NConfigProvider class="app-content" :theme="darkTheme" :locale="zhCN" :date-locale="dateZhCN" :theme-overrides="themeOverrides">
    <NGlobalStyle></NGlobalStyle>
    <router-view />
  </NConfigProvider>
</template>

<script setup lang="ts">
import {
  darkTheme,
  dateZhCN,
  NConfigProvider,
  zhCN,
  NGlobalStyle,
  type GlobalThemeOverrides,
} from "naive-ui";
import { ref, provide, onMounted, onUnmounted } from "vue";

/* 黑金主题：与扩展弹窗同源的香槟金 + 深琥珀底色 */
const GOLD = "#d8b25f";
const GOLD_HI = "#f6e3b6";
const GOLD_LO = "#a87c39";
const JADE = "#7ec9a4";
const BAD = "#cd6a53";
const ON_GOLD = "#241a09";

const themeOverrides = ref<GlobalThemeOverrides>({
  common: {
    primaryColor: GOLD,
    primaryColorHover: GOLD_HI,
    primaryColorPressed: GOLD_LO,
    primaryColorSuppl: GOLD,

    infoColor: "#c9a259",
    infoColorHover: "#e3c07c",
    infoColorPressed: "#9c7a38",
    infoColorSuppl: "#c9a259",

    successColor: JADE,
    successColorHover: "#9adcb9",
    successColorPressed: "#4f9e76",
    successColorSuppl: JADE,

    warningColor: "#d9a24f",
    warningColorHover: "#efc173",
    warningColorPressed: "#b07f33",
    warningColorSuppl: "#d9a24f",

    errorColor: BAD,
    errorColorHover: "#e48e76",
    errorColorPressed: "#a54e3a",
    errorColorSuppl: BAD,

    textColorBase: "#efe6d4",
    textColor1: "#efe6d4",
    textColor2: "#cbbda2",
    textColor3: "#9b8f79",
    textColorDisabled: "#6d6454",
    placeholderColor: "#6d6454",
    placeholderColorDisabled: "#57503f",

    borderColor: "rgba(216,178,95,.20)",
    dividerColor: "rgba(216,178,95,.14)",

    bodyColor: "transparent",
    cardColor: "rgba(23,19,14,.72)",
    modalColor: "#17130e",
    popoverColor: "#1a150f",
    tableColor: "transparent",
    inputColor: "rgba(255,240,210,.035)",
    inputColorDisabled: "rgba(255,240,210,.02)",
    actionColor: "rgba(255,240,210,.04)",
    hoverColor: "rgba(216,178,95,.08)",
    pressedColor: "rgba(216,178,95,.14)",
    baseColor: "#0e0c0a",

    scrollbarColor: "rgba(216,178,95,.22)",
    scrollbarColorHover: "rgba(216,178,95,.40)",

    borderRadius: "10px",
    borderRadiusSmall: "8px",

    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif',
    fontFamilyMono: '"SF Mono", "JetBrains Mono", "Fira Code", ui-monospace, Consolas, monospace',
    fontWeightStrong: "700",
  },

  Button: {
    textColorPrimary: ON_GOLD,
    textColorHoverPrimary: ON_GOLD,
    textColorPressedPrimary: ON_GOLD,
    textColorFocusPrimary: ON_GOLD,
    colorPrimary: GOLD,
    colorHoverPrimary: "#e8c98a",
    colorPressedPrimary: GOLD_LO,
    colorFocusPrimary: "#e8c98a",
    borderPrimary: "1px solid rgba(255,240,205,.55)",
    borderHoverPrimary: "1px solid rgba(255,246,224,.70)",
    borderPressedPrimary: "1px solid rgba(255,240,205,.45)",
    borderFocusPrimary: "1px solid rgba(255,246,224,.70)",

    textColorSuccess: "#0d1a13",
    textColorHoverSuccess: "#0d1a13",
    textColorPressedSuccess: "#0d1a13",
    textColorFocusSuccess: "#0d1a13",

    textColorError: "#f6ddd5",
    textColorHoverError: "#fff2ec",
    textColorPressedError: "#e8b6a6",
    textColorFocusError: "#fff2ec",

    textColorWarning: "#241a09",
    textColorHoverWarning: "#241a09",
    textColorPressedWarning: "#241a09",
    textColorFocusWarning: "#241a09",

    textColorInfo: "#241a09",
    textColorHoverInfo: "#241a09",
    textColorPressedInfo: "#241a09",
    textColorFocusInfo: "#241a09",
  },

  Input: {
    color: "rgba(255,240,210,.035)",
    colorFocus: "rgba(255,240,210,.07)",
    colorDisabled: "rgba(255,240,210,.02)",
    textColor: "#efe6d4",
    textColorDisabled: "#6d6454",
    placeholderColor: "#6d6454",
    caretColor: GOLD,
    border: "1px solid rgba(216,178,95,.20)",
    borderHover: "1px solid rgba(216,178,95,.38)",
    borderFocus: "1px solid rgba(240,208,138,.58)",
    borderDisabled: "1px solid rgba(216,178,95,.10)",
    boxShadowFocus: "0 0 0 3px rgba(216,178,95,.13)",
  },

  InputNumber: {
    peers: {
      Input: {
        color: "rgba(255,240,210,.035)",
        colorFocus: "rgba(255,240,210,.07)",
        textColor: "#f2d9a4",
        placeholderColor: "#6d6454",
        caretColor: GOLD,
        border: "1px solid rgba(216,178,95,.20)",
        borderHover: "1px solid rgba(216,178,95,.38)",
        borderFocus: "1px solid rgba(240,208,138,.58)",
        boxShadowFocus: "0 0 0 3px rgba(216,178,95,.13)",
      },
    },
  },

  Switch: {
    railColor: "rgba(255,240,210,.12)",
    railColorActive: "#c9a259",
    railColorHover: "rgba(255,240,210,.18)",
    buttonColor: "#f6e3b6",
    boxShadowFocus: "0 0 0 3px rgba(216,178,95,.16)",
  },

  Tabs: {
    barColor: GOLD,
    tabTextColor: "#9b8f79",
    tabTextColorHover: "#d8c9ab",
    tabTextColorActive: GOLD_HI,
    tabTextColorBar: GOLD_HI,
    tabFontWeightActive: "700",
    tabBorderColor: "rgba(216,178,95,.14)",
  },

  Modal: {
    color: "#14110d",
    titleTextColor: "#efe6d4",
    textColor: "#cbbda2",
    boxShadow: "0 30px 80px -30px #000, 0 0 60px -30px rgba(216,178,95,.35)",
    borderRadius: "16px",
  },

  Popover: {
    color: "#1a150f",
    textColor: "#cbbda2",
    boxShadow: "0 18px 40px -22px #000, 0 0 32px -18px rgba(216,178,95,.32)",
    borderRadius: "12px",
  },

  Select: {
    peers: {
      InternalSelection: {
        color: "rgba(255,240,210,.035)",
        colorActive: "rgba(255,240,210,.07)",
        textColor: "#efe6d4",
        placeholderColor: "#6d6454",
        border: "1px solid rgba(216,178,95,.20)",
        borderHover: "1px solid rgba(216,178,95,.38)",
        borderActive: "1px solid rgba(240,208,138,.58)",
        borderFocus: "1px solid rgba(240,208,138,.58)",
        boxShadowActive: "0 0 0 3px rgba(216,178,95,.13)",
        boxShadowFocus: "0 0 0 3px rgba(216,178,95,.13)",
        arrowColor: "#9b8f79",
      },
      InternalSelectMenu: {
        color: "#1a150f",
        optionTextColor: "#cbbda2",
        optionTextColorActive: GOLD_HI,
        optionColorPending: "rgba(216,178,95,.10)",
        optionColorActive: "rgba(216,178,95,.14)",
        optionCheckColor: GOLD,
      },
    },
  },

  Pagination: {
    itemColorActive: GOLD,
    itemColorActiveHover: "#e8c98a",
    itemTextColorActive: ON_GOLD,
    itemTextColorActiveHover: ON_GOLD,
    itemColorHover: "rgba(216,178,95,.10)",
    itemBorderActive: "1px solid rgba(246,227,182,.55)",
    itemBorderRadius: "8px",
  },

  Tag: {
    borderRadius: "999px",
  },

  Collapse: {
    titleTextColor: "#cbbda2",
    titleTextColorHover: GOLD_HI,
    arrowColor: "#9b8f79",
    titleFontWeight: "600",
  },

  Message: {
    color: "#17130e",
    textColor: "#efe6d4",
    borderRadius: "12px",
    boxShadow: "0 18px 40px -22px #000, 0 0 30px -18px rgba(216,178,95,.35)",
  },
});

// 统一调试模式：Ctrl+Shift+D 一键切换所有快捷显示（收益统计/高级设置/线路地址/卡牌真实模式）
const debugMode = ref(false);
provide('debugMode', debugMode);

function handleKeydown(e: KeyboardEvent) {
  if (e.ctrlKey && e.shiftKey && e.key === 'D') {
    e.preventDefault();
    debugMode.value = !debugMode.value;
    localStorage.setItem('kp_debugMode', debugMode.value ? '1' : '0');
  }
}

onMounted(() => {
  debugMode.value = localStorage.getItem('kp_debugMode') === '1';
  window.addEventListener('keydown', handleKeydown);
});
onUnmounted(() => window.removeEventListener('keydown', handleKeydown));
</script>
<style lang="scss">
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

#app {
  width: 100%;
  height: 100%;
  overflow: hidden;

  .app-content {
    width: 100%;
    height: 100%;
  }
}
</style>
